import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { Divider } from "@msuite/ui/divider";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { moment } from "@msuite/utils/date";
import { formatNumber } from "@msuite/utils/numbers";
import { guardString } from "@msuite/utils/strings";
import {
	getWebfleetObjectTypeTranslationKey,
	getWebfleetTripmodeTranslationKey,
} from "@msuite/utils/vehicles";
import React from "react";
import { VehicleFuel } from "@/components/vehicles";
import { useVehicleIntegration } from "@/hooks/use-vehicle-integration";
import type { VehiclePanelBaseProps } from "../../shared";

export function VehicleWebfleetInformation({ vehicleId }: VehiclePanelBaseProps) {
	/** Hooks */
	const { data, hasIntegration, dataUpdatedAt } = useVehicleIntegration(vehicleId);
	const { t } = useTranslation();

	/** Guard */
	if (!hasIntegration) return null;

	/** Render */
	return (
		<ContentCard
			title="Webfleet"
			icon={icon("Plug")}
			layoutId="vehicle-webfleet-information"
		>
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("general.name")}</DataGrid.Label>
					<DataGrid.Value>
						{guardString(data?.integrationData?.resource?.objectname)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("vehicles.ignition")}</DataGrid.Label>
					<DataGrid.Value>
						{data?.integrationData?.resource?.ignition
							? t("vehicles.ignition_on")
							: t("vehicles.ignition_off")}
					</DataGrid.Value>
				</DataGrid.Row>
				{data?.integrationData?.resource?.dest_text && (
					<React.Fragment>
						<DataGrid.Row>
							<DataGrid.Label>{t("webfleet.destination")}</DataGrid.Label>
							<DataGrid.Value>
								{guardString(data?.integrationData?.resource?.dest_text)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Divider />
					</React.Fragment>
				)}
				<DataGrid.Row>
					<DataGrid.Label>{t("general.type")}</DataGrid.Label>
					<DataGrid.Value>
						{t(getWebfleetObjectTypeTranslationKey(data?.integrationData?.resource?.objecttype))}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("vehicles.driver_name")}</DataGrid.Label>
					<DataGrid.Value>
						{guardString(data?.integrationData?.resource?.drivername)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("webfleet.tripmode.tripmode")}</DataGrid.Label>
					<DataGrid.Value>
						{t(getWebfleetTripmodeTranslationKey(data?.integrationData?.resource?.tripmode))}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("vehicles.speed")}</DataGrid.Label>
					<DataGrid.Value>{data?.integrationData?.resource?.speed || 0} km/h</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("webfleet.odometer")}</DataGrid.Label>
					<DataGrid.Value>
						{formatNumber((data?.integrationData?.resource?.odometer || 0) / 10, "kilometers")}
					</DataGrid.Value>
				</DataGrid.Row>
			</DataGrid>
			<Divider
				variant="content-card"
				className="my-2"
			/>
			<VehicleFuel permille={data?.integrationData?.resource?.fuellevel} />
			<Divider
				variant="content-card"
				className="my-2"
			/>
			<p className="label">
				{t("general_fields.updated_at")}:{" "}
				{moment(new Date(dataUpdatedAt).toISOString()).longFormat()}
			</p>
		</ContentCard>
	);
}
